import { Button } from '@travelnest/merlin.button';
import { startCase } from 'lodash';
import * as React from 'react';
import { useEffect } from 'react';
import api from '../../../api';
import { ICustomer } from '../../../data/entities/customer';
import { IEventV2 } from '../../../data/entities/event';
import { IProperty } from '../../../data/entities/property';
import { useAsyncRequest } from '../../helpers/hooks';
import { breakdownHasSucceeded } from '../../pages/property/bookings/details/helpers/helpers';
import { FormLabel, FormSelect } from '../form';
import { Content } from '../page/content/Content';
import { Panel } from '../panel/Panel';
import { defaultEmailTemplateId, emailTemplates, IEmailTemplateInfo } from './emailTemplates';

export interface ICustomerServiceEmailsComponentProps {
	event: IEventV2;
	customer: ICustomer;
	property: IProperty;
	onSendComplete: (success: boolean, templateId: string, templateInfo: IEmailTemplateInfo, event: IEventV2) => void;
}

export const CustomerServiceEmailsComponent: React.FunctionComponent<ICustomerServiceEmailsComponentProps> = (props) => {
	const [activeEmailTemplateId, setActiveEmailTemplateId] = React.useState(defaultEmailTemplateId);
	const [templateConfig, setTemplateConfig] = React.useState({});

	const [sendingStatus, send] = useAsyncRequest(async () => {
		const bookingId = props.event.id;
		const propertyId = props.event.property_id;

		return await api.notifications.createNotification(bookingId, {
			'property_id': propertyId,
			'booking_id': bookingId,
			...templateConfig
		}, activeEmailTemplateId, ['email'], null);
	});

	const activeEmailTemplate = emailTemplates.get(activeEmailTemplateId);

	const setTemplateOption = (key: string, value: any) => setTemplateConfig({ ...templateConfig, [key]: value });
	useEffect(() => {
		const configMap = {};

		if (activeEmailTemplate?.options) {
			for (const key of Object.keys(activeEmailTemplate?.options)) {
				configMap[key] = 'undefined';
			}
		}

		setTemplateConfig(configMap);
	}, [activeEmailTemplateId]);

	const hasUnselectedOptions = Object.values(templateConfig).includes('undefined');

	const confirmSend = () => {
		if (!activeEmailTemplate) {
			return;
		}

		const message =
			`Are you sure you want to send ${activeEmailTemplate.name} email to the ${activeEmailTemplate.target}?`;

		if (window.confirm(message)) {
			send().then(() => props.onSendComplete(
				true,
				activeEmailTemplateId,
				{ ...activeEmailTemplate, options: templateConfig },
				props.event
			));
		}
	};

	const breakdownSucceeded = props.event.booking && props.event.booking.value.breakdown
		? breakdownHasSucceeded(props.event.booking.value.breakdown)
		: false;

	const buttonText = !!props.event.notes && props.event.notes.some((note) => note.content.includes(activeEmailTemplateId))
		? 'Resend'
		: 'Send';

	return <Content type="full">
		<h2>Emails</h2>

		<p>Save any changes to this booking before sending an email to ensure these details are correct.</p>

		{!breakdownSucceeded ?
			<Panel type="error" withIcon={true}>These emails may be inaccurate because we couldn't generate a breakdown.</Panel>
			: ''}

		<div className="booking-emails__side-by-side">
			<FormSelect onChange={(e) => setActiveEmailTemplateId(e.target.value)}>
				{Array.from(emailTemplates.entries(), ([key, value]) => <option value={key} key={key}>To {value.target} - {value.name}</option>)}
			</FormSelect>

			<Button
				onClick={() => confirmSend()}
				disabled={hasUnselectedOptions || sendingStatus.isLoading}
			>
				{sendingStatus.isLoading ?
					'Sending' :
					`${buttonText}${activeEmailTemplate ? ` to ${activeEmailTemplate.target}` : ''}`
				}
			</Button>
		</div>

		{
			activeEmailTemplate?.options &&
			Object.entries(activeEmailTemplate.options).map(([optionName, option]) =>
				option(props.event) ?
					<div key={optionName} className="booking-emails__dropdown">
						<FormLabel htmlFor={optionName}>{startCase(optionName)}</FormLabel>
						<FormSelect onChange={(e) => setTemplateOption(optionName, e.target.value)}>
							<option value="undefined">Select {startCase(optionName)}</option>
							{
								Object.entries(option(props.event) || {}).map(([key, value]) => <option value={key} key={key}>{value}</option>)
							}
						</FormSelect>
					</div> : <></>
			)
		}
	</Content>;
};
